import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CustomButton from '../../components/buttons/CustomButton';
import { buildFiscalFilename } from '../../api/FiscalAPI';
import { getAccessToken } from '../../api/authTokens';
import styles from './FiscalInvoiceHistory.module.css';

/**
 * Configuração visual dos status históricos
 */
const HISTORY_STATUS_CONFIG = {
  canceled: {
    label: 'Cancelada',
    icon: <CloseOutlinedIcon fontSize="inherit" />,
    tagClass: styles['historyStatusTag--canceled'],
  },
  error: {
    label: 'Erro',
    icon: <PriorityHighOutlinedIcon fontSize="inherit" />,
    tagClass: styles['historyStatusTag--error'],
  },
  issued: {
    label: 'Emitida',
    icon: <CheckOutlinedIcon fontSize="inherit" />,
    tagClass: styles['historyStatusTag--issued'],
  },
  processing: {
    label: 'Em processamento',
    icon: <AutorenewOutlinedIcon fontSize="inherit" />,
    tagClass: styles['historyStatusTag--processing'],
  },
  queued: {
    label: 'Na fila',
    icon: <AutorenewOutlinedIcon fontSize="inherit" />,
    tagClass: styles['historyStatusTag--queued'],
  },
};

/**
 * Formata uma data ISO/SQL para exibição no formato dd/mm/aaaa HH:MM
 */
function formatDateTime(isoString) {
  if (!isoString) return null;
  try {
    const d = new Date(isoString.replace(' ', 'T'));
    const day   = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year  = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins  = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return isoString;
  }
}

/**
 * Faz download de um arquivo fiscal (PDF ou XML) de uma nota histórica.
 * Usa fetch com Authorization header para endpoints protegidos.
 * O nome do arquivo é construído a partir dos dados da nota (invoice).
 * @param {object} invoice - Objeto da nota fiscal com issued_at, patient_name, invoice_number, id
 * @param {'pdf'|'xml'} type
 */
function downloadHistoricalFile(invoice, type) {
  const token    = getAccessToken();
  const url      = `/api/fiscal/download_fiscal_file.php?invoiceId=${invoice.id}&type=${type}`;
  const filename = buildFiscalFilename(invoice, type);
  fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  })
    .then(res => {
      if (!res.ok) throw new Error('Arquivo não disponível.');
      return res.blob();
    })
    .then(blob => {
      const blobUrl  = window.URL.createObjectURL(blob);
      const link     = document.createElement('a');
      link.href      = blobUrl;
      link.download  = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(() => {});
}

/**
 * Item individual do histórico de notas
 */
function FiscalInvoiceHistoryItem({ invoice }) {
  const statusCfg = HISTORY_STATUS_CONFIG[invoice.status] || HISTORY_STATUS_CONFIG.canceled;

  // Data do evento sem repetir o estado (a tag já informa o status)
  const eventDate = invoice.canceled_at
    ? formatDateTime(invoice.canceled_at)
    : formatDateTime(invoice.issued_at || invoice.created_at);

  const dateLabel = eventDate
    ? eventDate.replace(' ', ' às ')
    : null;

  return (
    <div className={styles.historyItem}>
      {/* Linha principal: [tag status] [valor] [data] */}
      <div className={styles.historyItemMain}>
        {/* Tag colorida por status */}
        <span className={`${styles.historyStatusTag} ${statusCfg.tagClass}`}>
          <span className={styles.historyStatusTagIcon}>{statusCfg.icon}</span>
          {statusCfg.label}
        </span>

        {/* Valor monetário */}
        {invoice.amount != null && (
          <span className={styles.historyItemAmount}>
            R$ {parseFloat(invoice.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </span>
        )}

        {/* Data/hora do evento */}
        {dateLabel && (
          <span className={styles.historyItemDate}>{dateLabel}</span>
        )}
      </div>

      {/* Número da NFS-e (linha secundária, quando disponível) */}
      {invoice.invoice_number && (
        <Typography variant="caption" className={styles.historyItemNfseNumber}>
          NFS-e nº {invoice.invoice_number}
        </Typography>
      )}

      {/* Mensagem de erro */}
      {invoice.error_message && (
        <Typography variant="caption" className={styles.historyItemError}>
          {invoice.error_message}
        </Typography>
      )}

      {/* Botões de download */}
      {(invoice.has_pdf || invoice.has_xml) && (
        <div className={styles.historyItemActions}>
          {invoice.has_pdf && (
            <CustomButton
              variant="outlined"
              style="secondary"
              label="PDF"
              icon={<DownloadOutlinedIcon />}
              onClick={() => downloadHistoricalFile(invoice, 'pdf')}
            />
          )}
          {invoice.has_xml && (
            <CustomButton
              variant="outlined"
              style="secondary"
              label="XML"
              icon={<DownloadOutlinedIcon />}
              onClick={() => downloadHistoricalFile(invoice, 'xml')}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FiscalInvoiceHistory
 *
 * Exibe o histórico de notas fiscais canceladas ou com erro de um atendimento.
 * Começa colapsado. Filtra automaticamente as notas históricas (canceled/error)
 * do array completo recebido via props.
 *
 * Props:
 *   invoices — array completo de notas do atendimento (incluindo a ativa)
 */
function FiscalInvoiceHistory({ invoices }) {
  // Começa expandido quando há notas históricas para garantir visibilidade
  const [expanded, setExpanded] = useState(true);

  // Exibe apenas notas que não estão ativas (canceled ou error)
  const historicalInvoices = (invoices || []).filter(inv =>
    ['canceled', 'error'].includes(inv.status)
  );

  if (historicalInvoices.length === 0) return null;

  return (
    <Box className={styles.historyContainer}>
      {/* Cabeçalho colapsável */}
      <button
        className={styles.historyToggle}
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
      >
        <Typography variant="subtitle2" className={styles.historyTitle}>
          Histórico de emissões ({historicalInvoices.length})
        </Typography>
        <IconButton size="small" tabIndex={-1} component="span">
          {expanded ? <ExpandLessOutlinedIcon fontSize="small" /> : <ExpandMoreOutlinedIcon fontSize="small" />}
        </IconButton>
      </button>

      {/* Lista de notas históricas */}
      <Collapse in={expanded}>
        <div className={styles.historyList}>
          {historicalInvoices.map(inv => (
            <FiscalInvoiceHistoryItem key={inv.id} invoice={inv} />
          ))}
        </div>
      </Collapse>
    </Box>
  );
}

export default FiscalInvoiceHistory;
